import LightBox from '@/components/LightBox/LightBox'
import {
	closestCenter,
	DndContext,
	DndContextProps,
	DragOverlay,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core'
import {
	horizontalListSortingStrategy,
	SortableContext,
	sortableKeyboardCoordinates,
} from '@dnd-kit/sortable'
import clsx from 'clsx'
import { findIndex, partial } from 'lodash-es'
import { useMemo, useState, type HTMLAttributes } from 'react'
import type { SlideImage as BaseSlideImage } from 'yet-another-react-lightbox'
import {
	AttachmentType,
	FileAttachment as FileAttachmentType,
} from '../../utils/fetchers/attachment'
import AttachmentSortContainer from './AttachmentSortContainer'
import FileAttachment from './FileAttachment'
import styles from './FileAttachmentGroup.module.css'

interface SlideImage extends BaseSlideImage {
	attachmentType: AttachmentType
	attachmentId: string
}

export interface FileAttachmentGroupProps extends HTMLAttributes<HTMLDivElement> {
	data: FileAttachmentType[]
	onDelete?: (id: string) => void
	onSwap?: (oldIndex: number, newIndex: number) => void
	onRetry?: (id: string) => void
}

/**
 * 展示附件
 */
export default function FileAttachmentGroup({
	data,
	onDelete,
	onRetry,
	onSwap,
	className,
	...props
}: FileAttachmentGroupProps) {
	const [draggingItem, setDraggingItem] = useState<FileAttachmentType | null | undefined>(null)
	const [open, setOpen] = useState(false)
	const [index, setIndex] = useState(0)

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	)

	const slides = useMemo(
		() =>
			data.map((item) => ({
				src: item.cdn?.file.fileUrl || '',
				width: item.metadata?.width,
				height: item.metadata?.height,
				attachmentType: item.type,
				attachmentId: item.id,
			})),
		[data]
	)

	const handleDragStart: DndContextProps['onDragStart'] = (event) => {
		setDraggingItem(data.find((item) => item.id === event.active.id))
	}

	const handleDragEnd: DndContextProps['onDragEnd'] = (event) => {
		const { active, over } = event

		if (over?.id && active.id !== over.id) {
			const oldIndex = findIndex(data, (item) => item.id === active.id)
			const newIndex = findIndex(data, (item) => item.id === over.id)

			onSwap?.(oldIndex, newIndex)
		}
		setDraggingItem(null)
	}

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={closestCenter}
			onDragStart={handleDragStart}
			onDragEnd={handleDragEnd}
		>
			<SortableContext items={data} strategy={horizontalListSortingStrategy}>
				<div className={clsx(styles.root, className)} {...props}>
					{data.map((item: FileAttachmentType, itemIndex: number) => (
						<AttachmentSortContainer id={item.id} key={item.id}>
							<FileAttachment
								onDelete={onDelete && partial(onDelete, item.id)}
								onRetry={onRetry && partial(onRetry, item.id)}
								data={item}
								onClick={() => {
									setIndex(itemIndex)
									setOpen(true)
								}}
							/>
						</AttachmentSortContainer>
					))}
				</div>
			</SortableContext>
			<DragOverlay>{draggingItem ? <FileAttachment data={draggingItem} /> : null}</DragOverlay>
			<LightBox
				open={open}
				close={() => setOpen(false)}
				index={index}
				slides={slides}
				render={{
					slide: ({ slide }) => {
						const item = slide as SlideImage
						if (item.attachmentType === AttachmentType.VIDEO) {
							return (
								<video
									src={item.src}
									width={item.width}
									height={item.height}
									controls
									muted
									playsInline
									autoPlay
									className={styles.video}
								/>
							)
						}
						return undefined
					},
				}}
			/>
		</DndContext>
	)
}
